import { BettingApi } from 'api';
import { ActionType } from 'api/betslipAnalytics.api';
import { MarketTypesState } from 'features/eventLive/interface/interface';
import { EventState, GamesState } from 'features/eventPreMatch/interface/sportPrematch';
import React, { useEffect } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQuickShotRefreshFeatureFlag } from 'shared/featureFlags';
import { useBetslipAnalytics } from 'shared/hooks/useBetslipAnalytics';
import { BETSType, CouponState, EventType, State } from 'shared/interface';
import { addBet } from 'shared/store/coupon.action';
import { isMobile, setAllBets } from 'shared/util/utility';
import { Mousewheel, SwiperOptions } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import SingleQuickShot from './singleQuickShot';

interface QuickShotsSwiperProps extends WithTranslation {
	quickShotMarketType?: MarketTypesState;
	eventDetailData: EventState;
	isLive: boolean;
}
interface quickShotProps {
	game: GamesState;
	loading: boolean;
}
const QuickShotsSwiper: React.FC<QuickShotsSwiperProps> = ({ quickShotMarketType, eventDetailData, t, isLive }) => {
	const [quickShots, setQuickShots] = React.useState<quickShotProps[]>([]);
	const coupon = useSelector<State, CouponState>((state) => state.coupon);
	const dispatch = useDispatch();
	const analytics = useSelector((state: State) => state.coupon.analytics);
	const bets = useSelector((state: State) => state.coupon.bets);
	const allBets = [...bets.blocks, ...bets.outcomes];
	const quickShotRefreshEnabled = useQuickShotRefreshFeatureFlag();
	const checkOddIsInBet = (outcomeId: number | undefined) => {
		return allBets.flat().some((bet) => bet.outcomeId === outcomeId) ?? [];
	};
	const { addAction } = useBetslipAnalytics();
	const location = useLocation();
	const addBetDispatch = (params: BETSType) => {
		dispatch(addBet(params));
	};
	const settings: SwiperOptions = {
		slidesPerView: isMobile() ? (quickShots.length > 1 ? 1.5 : 1) : quickShots.length > 3 ? 3.5 : quickShots.length === 3 ? 3 : 2,
		modules: [Mousewheel],
		allowTouchMove: true,
		grabCursor: true,
		threshold: 3,
		loop: false,
		spaceBetween: 8,
		preventClicks: true,
	};

	const initializeQuickShots = () => {
		const localQuickShots: quickShotProps[] = quickShotMarketType?.gameIds.map((game) => ({
			game: eventDetailData.games.find((item) => item.gameId === game),
			loading: quickShotRefreshEnabled.value,
		})) as quickShotProps[];
		const shots = localQuickShots.filter((item) => item.game !== undefined);
		setQuickShots(shots);
		if (!quickShotRefreshEnabled.value) return;
		fetchQuickShots(shots);
	};
	const fetchQuickShots = async (shots: quickShotProps[]) => {
		try {
			const refreshedGames: quickShotProps[] = [];
			for (let i = 0; i < shots.length; i++) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const shot = shots[i]!;
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const outcomeId = shot.game.outcomes[0]!.outcomeId;
				const response = await BettingApi.updateCustomBets(outcomeId);
				const outcome = response.data?.data?.outcome;
				if (response.data.isError || !outcome) throw response.data.message;
				setQuickShots((prev) =>
					prev.map((item) => {
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						if (item.game.outcomes[0]!.outcomeId === outcomeId) {
							//test for PK
							refreshedGames[i] = {
								game: {
									...item.game,
									// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
									outcomes: [{ ...item.game.outcomes[0]!, outcomeOdds: outcome.odds }],
								},
								loading: false,
							};
							//end test for PK
							return {
								game: {
									...item.game,
									// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
									outcomes: [{ ...item.game.outcomes[0]!, outcomeOdds: outcome.odds }],
								},
								loading: false,
							};
						} else {
							return item;
						}
					}),
				);
			}
		} catch (error) {
			setQuickShots((prev) =>
				prev.map((item) => {
					return {
						game: {
							...item.game,
						},
						loading: false,
					};
				}),
			);
		}
	};
	useEffect(() => {
		initializeQuickShots();
	}, []);

	const mergeNames = (gameName: string, outcomes: string) => {
		const gameNameParts = gameName.split(' | ');
		const outcomeNames = outcomes.split(' | ');

		const result = [];
		for (let i = 0; i < Math.max(gameNameParts.length, outcomeNames.length); i++) {
			if (gameNameParts[i]) result.push(gameNameParts[i]);
			if (outcomeNames[i]) result.push(outcomeNames[i]);
		}

		return result;
	};

	const addToBetSlip = (item: GamesState) => {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const isInBetslip = checkOddIsInBet(item.outcomes[0]!.outcomeId);
		const betObj = {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			outcomeId: item.outcomes[0]!.outcomeId as any,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			outcomeOdds: item.outcomes[0]!.outcomeOdds,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			outcomeName: item.outcomes[0]!.outcomeName,
			sportId: eventDetailData.sportId,
			eventId: eventDetailData.eventId,
			eventType: isLive ? EventType.LIVE : EventType.PREMATCH,
			gameName: item.gameName,
			gameType: item.gameType,
			categoryId: eventDetailData.categoryId,
			gameId: item.gameId,
			eventName: eventDetailData.eventName,
			league: `${eventDetailData.sportName} / ${eventDetailData.parentName} / ${eventDetailData.categoryName}`,
			selectedBet: true,
			banker: false,
			isLive: eventDetailData.isLive || false,
			combinationType: item.combinationType,
		};
		addAction(betObj, location.pathname, isInBetslip ? ActionType.REMOVE : ActionType.ADD);
		addBetDispatch({ ...bets, ...setAllBets(bets, betObj, dispatch, coupon, analytics, t) });
	};

	return (
		<>
			{quickShots && quickShots.length > 0 && (
				<Swiper {...settings} className="h-full w-full mb-3 !z-0">
					{quickShots.map((item, index) => (
						<SwiperSlide
							className={`!h-auto bg-primary-500 text-gray-100 flex flex-col p-2 rounded-4 text-paragraph-xs`}
							key={`placementsData-${index}`}
						>
							<SingleQuickShot
								loading={item.loading}
								splitGameName={mergeNames(item.game.gameName, item.game?.outcomes[0]?.outcomeName ?? '')}
								betValue={item.game?.outcomes[0]?.outcomeOdds}
								addToBetSlip={() => addToBetSlip(item.game)}
								isInBetslip={checkOddIsInBet(item.game?.outcomes[0]?.outcomeId)}
							/>
						</SwiperSlide>
					))}
				</Swiper>
			)}
		</>
	);
};

export default withTranslation()(QuickShotsSwiper);
